// Leaderboards: one ranking per measure, top 100 plus where you stand.
// The server rebuilds every board on a timer (server/boards.ts), never per
// request; a request reads the last build. Admin and dev-flagged accounts are
// left out of every board, as on the Records board.

// 'week': Weekly Catch (shared/weekcatch.ts), distinct species caught with a seal this UTC week.
export const BOARD_IDS = ['level', 'dex', 'best', 'auras', 'week', 'ladder', 'arena', 'guild'] as const;
export type BoardId = (typeof BOARD_IDS)[number];

export const BOARDS = {
  /** Rows sent per board. */
  top: 100,
  /** How often the server rebuilds every board. */
  refreshMs: 5 * 60_000,
} as const;

export const isBoardId = (x: unknown): x is BoardId => typeof x === 'string' && (BOARD_IDS as readonly string[]).includes(x);

/** One ranked entry before it is sent: key is the account (or the guild id on the guild board). */
export interface BoardEntry { key: number; value: number; tie?: number }

/**
 * Highest value first; equal values by `tie` (lower first, e.g. who got there first), then by key.
 * Ranks are shared on equal value (1, 1, 3). Entries at 0 or below are left out.
 */
export function rankEntries<T extends BoardEntry>(entries: readonly T[]): (T & { rank: number })[] {
  const sorted = entries.filter((e) => e.value > 0).sort((a, b) => b.value - a.value || (a.tie ?? 0) - (b.tie ?? 0) || a.key - b.key);
  const out: (T & { rank: number })[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const rank = i && sorted[i].value === sorted[i - 1].value ? out[i - 1].rank : i + 1;
    out.push({ ...sorted[i], rank });
  }
  return out;
}

// Monthly podium: the first rebuild of a UTC month gives last month's top 3 on
// every board but the Weekly Catch (which has its own weekly titles) a kept
// title for that board and month, e.g. "Gold · Auras · Oct 2026"; on the guild
// board every member of a placed guild gets it. The owner of the strongest
// creature (Power, as on hunts) gets the Crown chat badge. Looks only: never
// diamonds or coins. Every rebuild keeps a snapshot of the month's top places
// (kv 'podium-snap'), so the podium is last month's final standings even
// across a restart; kv 'month-podium' holds the last month given, so a month
// is given once. Admin and dev-flagged accounts are never placed.
export const PODIUM = {
  places: 3,
  medals: ['Gold', 'Silver', 'Bronze'],
  /** Board names in titles. */
  label: { level: 'Level', dex: 'Bestiary', best: 'Best Catch', auras: 'Auras', ladder: 'Ladder', arena: 'Arena', guild: 'Guild' } as Record<Exclude<BoardId, 'week'>, string>,
  badge: { id: 'crown', name: 'Crown', glyph: '♛' },
} as const;

export type PodiumBoard = Exclude<BoardId, 'week'>;
export const PODIUM_BOARDS = BOARD_IDS.filter((id): id is PodiumBoard => id !== 'week');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10" → "Oct 2026". */
export const monthLabel = (month: string) => `${MONTHS[Number(month.slice(5, 7)) - 1] ?? '?'} ${month.slice(0, 4)}`;

/** The kept title a podium place gives (rank 1..3; a shared rank shares the medal). */
export const monthTitle = (rank: number, board: PodiumBoard, month: string) => `${PODIUM.medals[rank - 1]} · ${PODIUM.label[board]} · ${monthLabel(month)}`;

/** A board's podium: its first PODIUM.places entries in board order (value, then tie, then key), each with its shared rank. */
export const podiumOf = <T extends BoardEntry>(entries: readonly T[]) => rankEntries(entries).slice(0, PODIUM.places);

/** The strongest creature: highest Power, then the lower creature id (the older catch). */
export function strongestOf<T extends { id: number; power: number }>(list: Iterable<T>): T | null {
  let best: T | null = null;
  for (const c of list) if (c.power > 0 && (!best || c.power > best.power || (c.power === best.power && c.id < best.id))) best = c;
  return best;
}

export interface PodiumPlace { key: number; name: string; value: number; rank: number }
export interface PodiumCrown { key: number; name: string; id: number; species: string; power: number }
/** A month's top places, rewritten on every rebuild of that month. */
export interface PodiumSnap { month: string; boards: Partial<Record<PodiumBoard, PodiumPlace[]>>; crown: PodiumCrown | null }
/** Last month's podium as the Ranks page shows it. */
export interface PodiumDTO { month: string; boards: Partial<Record<PodiumBoard, { rank: number; name: string; value: number }[]>>; crown: { name: string; species: string; power: number } | null }
